import React, { useCallback, useEffect, useState } from "react";
import type { FollowUpServiceGroup } from "../../../types";
import { servicesApi } from "../../../services";
import { getTodayInBrazil } from "../../../utils/dateUtils";
import { AdminPageHeader } from "../shared";
import { Icons } from "../../shared/Icons";
import { CreateAppointmentModal } from "../AdminAppointments/CreateAppointmentModal";
import { FollowUpClientsPanel } from "./FollowUpClientsPanel";

export const AdminFollowUpClientsPage: React.FC = () => {
  const [services, setServices] = useState<FollowUpServiceGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [ignoringKey, setIgnoringKey] = useState<string | null>(null);
  const [preFilledAppointmentData, setPreFilledAppointmentData] = useState<{
    clientId?: number;
    serviceId?: number;
    clientCarId?: number;
  }>();

  const loadFollowUpClients = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const res = await servicesApi.getFollowUpClients();
      if (res.success && res.data) {
        setServices(res.data.services);
      } else {
        setServices([]);
        setError(res.error || "Erro ao carregar clientes para retorno");
      }
    } catch (e) {
      setServices([]);
      setError(
        e instanceof Error ? e.message : "Erro ao carregar clientes para retorno",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFollowUpClients();
  }, [loadFollowUpClients]);

  const handleScheduleAppointment = (params: {
    clientId: number;
    serviceId: number;
    clientCarId?: number;
  }) => {
    setPreFilledAppointmentData(params);
    setCreateModalOpen(true);
  };

  const handleIgnoreSuggestion = async (params: {
    clientId: number;
    targetId: number;
    kind: "service" | "subService";
    lastAppointmentId: number;
  }) => {
    const key = `${params.kind}:${params.targetId}:${params.lastAppointmentId}`;
    setIgnoringKey(key);
    setError(null);

    try {
      const res = await servicesApi.ignoreFollowUpSuggestion(params);
      if (!res.success) {
        setError(res.error || "Erro ao ignorar sugestão de retorno");
        return;
      }

      setServices((prev) =>
        prev
          .map((group) => {
            if (
              group.serviceId !== params.targetId ||
              (group.kind ?? "service") !== params.kind
            ) {
              return group;
            }
            return {
              ...group,
              clients: group.clients.filter(
                (row) => row.lastAppointmentId !== params.lastAppointmentId,
              ),
            };
          })
          .filter((group) => group.clients.length > 0),
      );
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "Erro ao ignorar sugestão de retorno",
      );
    } finally {
      setIgnoringKey(null);
    }
  };

  const handleCloseAppointmentModal = () => {
    setCreateModalOpen(false);
    setPreFilledAppointmentData(undefined);
  };

  const handleAppointmentSuccess = () => {
    setCreateModalOpen(false);
    setPreFilledAppointmentData(undefined);
    loadFollowUpClients();
  };

  const totalClients = services.reduce(
    (sum, group) => sum + group.clients.length,
    0,
  );

  return (
    <div className="space-y-4 sm:space-y-6 md:space-y-8 animate-fade-in">
      <AdminPageHeader
        icon={<Icons.Phone sx={{ fontSize: 28 }} />}
        title="Retorno de Clientes"
        subtitle={
          loading
            ? "Carregando clientes com serviços vencidos…"
            : totalClients > 0
              ? `${totalClients} ${totalClients === 1 ? "cliente precisa" : "clientes precisam"} de retorno, agrupados por serviço.`
              : "Clientes que ultrapassaram a validade do serviço e precisam de acompanhamento."
        }
      />

      <FollowUpClientsPanel
        services={services}
        loading={loading}
        error={error}
        onRetry={loadFollowUpClients}
        onScheduleAppointment={handleScheduleAppointment}
        onIgnoreSuggestion={handleIgnoreSuggestion}
        ignoringKey={ignoringKey}
      />

      {createModalOpen && (
        <CreateAppointmentModal
          isOpen={createModalOpen}
          onClose={handleCloseAppointmentModal}
          onSuccess={handleAppointmentSuccess}
          selectedDate={getTodayInBrazil()}
          preFilledData={preFilledAppointmentData}
        />
      )}
    </div>
  );
};
